import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { LoadingOverlay, useAlert } from '../../components';
import { logout } from '../../services/authService';
import { getTransactions } from '../../services/firestoreService';
import { TRANSACTION_TYPES } from '../../config/firebase';
import { formatCurrency } from '../../utils/helpers';
import './ProfileScreen.css';

export const ProfileScreen = () => {
    const navigate = useNavigate();
    const { userData } = useAuth();
    const alert = useAlert();
    const [isInstalled, setIsInstalled] = useState(false);
    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState({
        totalIncome: 0,
        todayIncome: 0,
        teamIncome: 0
    });

    useEffect(() => {
        if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true) {
            setIsInstalled(true);
        }
        loadStatistics();
    }, [userData]);

    const loadStatistics = async () => {
        if (!userData) return;

        try {
            const transactions = await getTransactions(200);

            const totalIncome = transactions
                .filter(t => [TRANSACTION_TYPES.DAILY_INCOME, TRANSACTION_TYPES.REFERRAL_BONUS, TRANSACTION_TYPES.GIFT_CODE].includes(t.type))
                .reduce((sum, t) => sum + (t.amount || 0), 0);

            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const todayIncome = transactions
                .filter(t => {
                    if (![TRANSACTION_TYPES.DAILY_INCOME, TRANSACTION_TYPES.REFERRAL_BONUS].includes(t.type)) return false;
                    const transactionDate = t.createdAt?.toDate ? t.createdAt.toDate() : new Date(t.createdAt);
                    transactionDate.setHours(0, 0, 0, 0);
                    return transactionDate.getTime() === today.getTime();
                })
                .reduce((sum, t) => sum + (t.amount || 0), 0);

            const teamIncome = userData?.totalTeamEarnings || 0;

            setStats({
                totalIncome,
                todayIncome,
                teamIncome
            });
        } catch (error) {
            console.error('Error loading statistics:', error);
        }
    };

    const handleInstallClick = async () => {
        const promptEvent = window.deferredPrompt;
        if (!promptEvent) {
            if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true) {
                alert.showInfo('Installed', 'NEXTRON is already installed on your device!');
                setIsInstalled(true);
            } else {
                alert.showInfo('Preparing', 'Preparing installation... Please try again in a few seconds.');
            }
            return;
        }
        promptEvent.prompt();
        const { outcome } = await promptEvent.userChoice;
        if (outcome === 'accepted') {
            window.deferredPrompt = null;
            setIsInstalled(true);
            alert.showSuccess('Success', 'App installed successfully!');
        }
    };

    const handleLogout = () => {
        alert.showConfirm(
            'Logout',
            'Are you sure you want to logout?',
            async () => {
                try {
                    await logout();
                    navigate('/login');
                } catch (error) {
                    alert.showError('Error', 'Failed to logout');
                }
            }
        );
    };

    const menuItems = [
        { icon: 'shopping_bag', label: 'My Products', route: '/my-products' },
        { icon: 'schedule', label: 'Transaction History', route: '/transactions' },
        { icon: 'account_balance', label: 'Bank Account', route: '/bank-details' },
        { icon: 'admin_panel_settings', label: 'Security Centre', route: '/security' },
        { icon: 'redeem', label: 'Gift Code', route: '/gift-code' },
        { icon: 'info', label: 'About Us', route: '/about' },
        { icon: 'headset_mic', label: 'Customer Service', route: '/support' },
        {
            icon: 'add_to_home_screen',
            label: 'Install App',
            action: 'install',
            hide: isInstalled
        },
    ];

    return (
        <div className="profile-screen-modern">
            <div className="profile-header-new">
                <h1 className="profile-title-main">MY ACCOUNT</h1>

                <div className="user-profile-hero">
                    <div className="avatar-circle-big">
                        {userData?.phoneNumber ? userData.phoneNumber.substring(userData.phoneNumber.length - 1) : '8'}
                    </div>
                    <div className="user-text-info">
                        <h2 className="user-phone-number">{userData?.phoneNumber || '8445168224'}</h2>
                        <span className="partner-badge">PREMIUM PARTNER</span>
                    </div>
                </div>
            </div>

            <div className="stats-dashboard-card">
                <div className="stats-row-top">
                    <div className="stat-unit">
                        <span className="stat-label-small">RECHARGE BALANCE</span>
                        <span className="stat-value-mid">{formatCurrency(userData?.rechargeBalance || 0)}</span>
                    </div>
                    <div className="stat-unit border-left">
                        <span className="stat-label-small">WITHDRAWAL BAL</span>
                        <span className="stat-value-mid">{formatCurrency(userData?.withdrawBalance || 0)}</span>
                    </div>
                </div>

                <div className="stats-row-bottom">
                    <div className="stat-unit-mini">
                        <span className="stat-label-mini">TOTAL INC</span>
                        <span className="stat-value-mini">{formatCurrency(stats.totalIncome)}</span>
                    </div>
                    <div className="stat-unit-mini border-left">
                        <span className="stat-label-mini">TODAY INC</span>
                        <span className="stat-value-mini">{formatCurrency(stats.todayIncome)}</span>
                    </div>
                    <div className="stat-unit-mini border-left">
                        <span className="stat-label-mini">TEAM INC</span>
                        <span className="stat-value-mini">{formatCurrency(stats.teamIncome)}</span>
                    </div>
                </div>
            </div>

            <div className="profile-actions-row">
                <button className="p-action-btn recharge" onClick={() => navigate('/recharge')}>
                    <span className="material-icons">wallet</span>
                    Recharge
                </button>
                <button className="p-action-btn withdrawal" onClick={() => navigate('/withdraw')}>
                    <span className="material-icons">sync_alt</span>
                    Withdrawal
                </button>
            </div>

            <div className="profile-menu-container">
                {menuItems.filter(item => !item.hide).map((item, index) => (
                    <div
                        key={index}
                        className="profile-menu-item"
                        onClick={() => item.action === 'install' ? handleInstallClick() : navigate(item.route)}
                    >
                        <div className="menu-item-left">
                            <span className="material-icons menu-icon-main">{item.icon}</span>
                            <span className="menu-label-text">{item.label}</span>
                        </div>
                        <span className="material-icons menu-chevron">chevron_right</span>
                    </div>
                ))}

                <div className="profile-menu-item logout-item" onClick={handleLogout}>
                    <div className="menu-item-left">
                        <span className="material-icons menu-icon-main">logout</span>
                        <span className="menu-label-text">Logout</span>
                    </div>
                </div>
            </div>

            <div className="app-version-footer">
                <p>NEXTRON v1.0.9</p>
            </div>
        </div>
    );
};

export default ProfileScreen;
